/**
 * ServicesHomePage — the marketing/landing page for SocialPulse Services™.
 * Explains the "Tell us what you need" promise and lets visitors either
 * (a) start a request, (b) list themselves as a provider, or
 * (c) browse categories.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { httpPublic } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Users, Search, Store } from "lucide-react";
import RequestServiceWidget from "@/components/RequestServiceWidget";

export default function ServicesHomePage() {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    httpPublic.get("/sp-services/categories").then(r => setCategories(r.data.categories || []));
  }, []);

  const roots = categories.filter(c => !c.parent_slug);

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="services-home-page">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-emerald-700 mb-3">
          <Sparkles className="w-3.5 h-3.5" />SocialPulse Services
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
          Tell us what you need.<br />We'll find someone who can do it.
        </h1>
        <p className="text-slate-600 mt-4 text-base">
          One request goes to up to 5 qualified providers from across the SocialPulse network — travel, medical support, family & events, and more.
        </p>
      </div>

      <div className="max-w-xl mx-auto mb-14">
        <RequestServiceWidget
          title="Describe your need in your own words"
          subtitle="Type it however feels natural — dates, places, languages, anything. We'll fill in the rest."
        />
      </div>

      <div className="grid gap-6 sm:grid-cols-3 mb-14">
        <Feature icon={<Search className="w-5 h-5" />} title="1. Describe" text="Tell us what you need in plain language." />
        <Feature icon={<Users className="w-5 h-5" />} title="2. Get matched" text="Up to 5 qualified providers see your request." />
        <Feature icon={<Store className="w-5 h-5" />} title="3. Choose & book" text="Compare quotes, message, book privately." />
      </div>

      <div className="mb-14">
        <h2 className="text-lg font-semibold mb-4">Browse categories</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          {roots.map(rc => (
            <Card key={rc.slug} className="p-5">
              <div className="font-semibold">{rc.name}</div>
              <div className="text-xs text-slate-600 mt-1">{rc.description}</div>
              <div className="mt-3 flex flex-wrap gap-1">
                {categories.filter(c => c.parent_slug === rc.slug).slice(0, 4).map(c => (
                  <Link
                    key={c.slug}
                    to={`/hire/request?category=${c.slug}`}
                    className="text-xs px-2 py-1 rounded-full border border-slate-200 hover:border-emerald-400 hover:text-emerald-700"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Card className="p-6 sm:p-8 bg-slate-900 text-slate-100">
        <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold">Offer your services on SocialPulse</h3>
            <p className="text-sm text-slate-300 mt-1">Free during Phase 1 · leads across the entire SocialPulse network.</p>
          </div>
          <Link to="/hire/provider">
            <Button variant="secondary" data-testid="become-provider-cta">Become a provider</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div>
      <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mb-3">{icon}</div>
      <div className="font-semibold">{title}</div>
      <div className="text-sm text-slate-600 mt-1">{text}</div>
    </div>
  );
}
